import React from "react";
import { motion } from "framer-motion";
import Films from "./ProjectGrid"; // the Films page component
import News from "./News";
import Contact from "./Contact"; // change if your Contact page file has a different name
import aboutImage from "../assets/StartFilm.png";

// Same animation style as the About page
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.3 },
  },
};

const textVariants = {
  hidden: { opacity: 0, x: -30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.8, ease: [0.215, 0.61, 0.355, 1] },
  },
};

const imageVariants = {
  hidden: { opacity: 0, scale: 1.1 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 1.8, ease: [0.33, 1, 0.68, 1] },
  },
};

export default function Main() {
  return (
    <main className="horizontal-master">
      <div className="scroll-track">

        {/* INTRO SECTION — same text + image as the About page, right after the header */}
        <motion.section
          className="home-about"
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          <div className="home-about-container">
            {/* LEFT CONTENT */}
            <div className="home-about-text">
              <motion.h2 variants={textVariants} className="home-about-title">
                About Us
              </motion.h2>

              <div>
                <motion.p variants={textVariants}>
                  Sippy Films is an independent studio focused on producing,
                  financing, and distributing premium content for a global audience.
                  Founded in 1951 by legendary film producer G. P. Sippy, the studio
                  has produced many of Indian cinema’s most memorable films
                  over seven decades, including <em>Seeta Aur Geeta, Sholay, Shaan,</em> and <em>Saagar.</em>
                  The company has launched many of today’s stars and acting legends
                  including Shah Rukh Khan, Rajesh Khanna, Babita Kapoor, and Raveena Tandon.
                </motion.p>

                <motion.p variants={textVariants}>
                  In 1975, the company released Sholay the first Indian film in 70mm
                  along with stereophonic six-track sound, pushing the boundaries of technology.
                </motion.p>

                <motion.p variants={textVariants}>
                  Today, Sippy Films strives to continue the vision its founder
                  established, introducing new talent and pushing storytelling through innovation.
                </motion.p>
              </div>
            </div>

            {/* RIGHT IMAGE */}
            <div className="home-about-image-wrapper">
              <motion.div className="home-image-reveal" variants={imageVariants}>
                <img src={aboutImage} alt="Sippy Films legacy collage" className="home-about-img" />
              </motion.div>
              <div className="home-image-frame" />
            </div>
          </div>
        </motion.section>

        {/* FILMS SECTION */}
        <section className="home-films">
          <Films />
        </section>

        {/* NEWS SECTION */}
        <section className="home-news">
          <News />
        </section>

        {/* CONTACT SECTION */}
        <section className="home-contact">
          <Contact />
        </section>

      </div>

      <style>{`
        :root { --gold: #c5a059; --dark: #000; }
        body { margin: 0; background: var(--dark); color: #fff; font-family: 'Sippy', sans-serif; overflow-x: hidden; }

        /* General Layout */
        .horizontal-master { width: 100%; min-height: 100vh; overflow-x: hidden; }
        .scroll-track { display: flex; flex-direction: column; width: 100%; height: auto; }

        /* About-style intro (mirrors the About page) */
        .home-about {
          background-color: #050505;
          color: #fff;
          display: flex;
          align-items: center;
          position: relative;
          overflow: hidden;
          padding: 120px 5% 80px;
        }

        .home-about-container {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px;
          align-items: center;
          max-width: 1400px;
          margin: auto;
          z-index: 2;
        }

        .home-about-title {
          font-family: 'SippyBold', 'Sippy', sans-serif;
          font-size: clamp(3rem, 5vw, 5rem);
          text-transform: uppercase;
          letter-spacing: -2px;
          margin: 0 0 40px 0;
          line-height: 1;
        }

        .home-about-text p {
          font-size: 1.1rem;
          line-height: 1.4;
          color: rgba(255, 255, 255, 0.7);
          margin: 0 0 25px 0;
          max-width: 550px;
        }

        .home-about-image-wrapper { position: relative; width: 100%; height: 70vh; }

        .home-image-reveal {
          width: 100%;
          height: 100%;
          overflow: hidden;
          box-shadow: 0 30px 60px rgba(0, 0, 0, 0.8);
          background-color: #000;
        }

        .home-about-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          filter: grayscale(20%);
          transition: filter 2s ease;
        }
        .home-about:hover .home-about-img { filter: grayscale(0%); }

        .home-image-frame {
          position: absolute;
          inset: -20px;
          border: 1px solid #26b8eb;
          z-index: -1;
          pointer-events: none;
        }

        /* Sections below About: these pages normally sit under the fixed navbar,
           so they carry extra top padding. On the home page they follow another
           section, so trim it. */
        .home-films { width: 100%; }
        .home-films .projects-container { padding-top: 60px; min-height: auto; }
        .scroll-track .news-page { padding-top: 60px; }

        /* Tablet / Mobile */
        @media (max-width: 1024px) {
          .home-about { padding: 100px 5% 50px; }
          .home-about-container { grid-template-columns: 1fr; gap: 50px; }
          .home-about-image-wrapper { height: 400px; order: -1; }
        }
        @media (max-width: 768px) {
          .horizontal-master { height: auto; overflow: visible; }
          .scroll-track { display: block; width: 100%; height: auto; }
        }
      `}</style>
    </main>
  );
}